import { ChangeDetectionStrategy, Component, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, of, Subject } from 'rxjs';
import { finalize, switchMap, take } from 'rxjs/operators';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { DocumentTypeMimeTypes } from '../../../core/model/const/document-type-mime-types';
import { LocalDate } from '../../../core/model/local-date';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { SetHomeqOfficesFormComponent } from '../../../rent/component/set-homeq-offices-form/set-homeq-offices-form.component';
import { BuildingDocumentType, RentalObjectDocumentType } from '../../../rent/model/const/documentEnums';
import { DwellingService } from '../../../rent/service/data/dwelling.service';
import { LettingAdvertismentPlatformType } from '../../model/const/enums';
import { LettingAdvertisment } from '../../model/letting-advertisment';
import { LettingShowing } from '../../model/letting-showing';
import { DhMarketOptions, HomeqOptions } from '../../model/platform-options';
import { IUploadedDocument } from '../../model/uploaded-document';
import { LettingAdvertismentFormCommandHandlerResolver } from '../../service/command/letting-advertisment-form-command-handler-resolver.service';
import { LettingAdvertismentService } from '../../service/letting-advertisment.service';
import { LettingAdvertismentBoplatsSverigeComponent } from '../letting-advertisment-boplats-sverige/letting-advertisment-boplats-sverige.component';
import { LettingAdvertismentHomeqComponent } from '../letting-advertisment-homeq/letting-advertisment-homeq.component';
import { LettingAdvertismentMarketAreaComponent } from '../letting-advertisment-market-area/letting-advertisment-market-area.component';
import { LettingAdvertismentMarketComponent } from '../letting-advertisment-market/letting-advertisment-market.component';
import { LanguageService } from '../../../core/service/language.service';

@Component({
  selector: 'app-letting-advertisment-form',
  templateUrl: './letting-advertisment-form.component.html',
  styleUrls: ['./letting-advertisment-form.component.scss'],
  providers: [
    { provide: DataService, useExisting: LettingAdvertismentService },
    DeleteFormCommandHandler,
    LettingAdvertismentFormCommandHandlerResolver,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LettingAdvertismentFormComponent extends FormComponentBase<LettingAdvertisment> {
  constructor(
    private readonly _dataService: LettingAdvertismentService,
    private readonly _cf: FormControlExFactory,
    private readonly _fb: FormBuilder,
    private readonly _route: ActivatedRoute,
    private readonly _accountService: UserAccountService,
    private readonly _dwellingService: DwellingService,
    private readonly _actionService: ActionWindowService,
    private readonly _injector: Injector,
    commandDispatcher: CommandDispatcher,
    handlerResolver: LettingAdvertismentFormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    private readonly _languageService: LanguageService
  ) {
    super(_route, _dataService, messageService, navigation, commandDispatcher, handlerResolver);
  }

  protected pdfOrImageMimes = DocumentTypeMimeTypes.PDF().concat(DocumentTypeMimeTypes.WebImage());
  protected imageMimes = DocumentTypeMimeTypes.WebImage();
  protected pdfMimes = DocumentTypeMimeTypes.PDF();

  protected homeQOfficeName: string = '';

  protected tenants: LettingShowing[];
  protected canPublishDhMarketToBoplatsSverige: boolean = false;

  protected RentalObjectDocumentType = RentalObjectDocumentType;
  protected BuildingDocumentType = BuildingDocumentType;

  protected createModel(): LettingAdvertisment {
    return new LettingAdvertisment();
  }

  platformSelection: SelectionItem[] = [];

  private willBeSentToPlatform: Subject<void>;
  protected isUnpublishingFromBoplatsSverige: boolean = false;
  protected currentLang: string = 'sv-SE';
  protected isAdvertismentLoaded: boolean = false;

  @ViewChild('homeqform') homeqform: LettingAdvertismentHomeqComponent;
  @ViewChild('marketform') marketform: LettingAdvertismentMarketComponent;
  @ViewChild('marketareaform') marketareaform: LettingAdvertismentMarketAreaComponent;
  @ViewChild('boplatsSverigeForm') boplatsSverigeForm: LettingAdvertismentBoplatsSverigeComponent;

  protected getFormGroup(): FormGroup {
    const readOnly = this.dataContext.approvedAt || !!this.dataContext.sentToPlatformAt ? true : false;
    const group = this._fb.group({
      id: this._cf.createHidden(this.dataContext.id),
      rentalObjectId: this._cf.createHidden(this.dataContext.rentalObjectId),
      rentalObjectName: this._cf.createWithOptions('COMMON.RENTAL_OBJECT', this.dataContext.rentalObjectName, { isReadonly: true }),
      platform: this._cf.createWithOptions('LETTING.ADVERTISMENT.PLATFORM', null, { required: true, isReadonly: readOnly }),
      approvedAt: this._cf.createReadonly('LETTING.ADVERTISMENT.APPROVED_AT', this.dataContext.approvedAt),
      buildingCommonAreasPhotos: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.COMMON_AREA_PHOTO',
        this.transformDocument(this.dataContext.buildingCommonAreasPhotos),
        { isReadonly: readOnly }
      ),
      buildingSurroundingsPhotos: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.SURROUNDINGS_PHOTO',
        this.transformDocument(this.dataContext.buildingSurroundingsPhotos),
        { isReadonly: readOnly }
      ),
      buildingFacadePhotos: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.FACADE_PHOTO',
        this.transformDocument(this.dataContext.buildingFacadePhotos),
        { isReadonly: readOnly }
      ),
      buildingSalesDocuments: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.SALES_DOCUMENT',
        this.transformDocument(this.dataContext.buildingSalesDocuments),
        { isReadonly: readOnly }
      ),
      rentalObjectPhotos: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.OBJECT_PHOTO',
        this.transformDocument(this.dataContext.rentalObjectDocuments),
        { isReadonly: readOnly }
      ),
      rentalObjectDocuments: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.INTERIOR_PHOTO',
        this.transformDocument(this.dataContext.rentalObjectDocuments),
        { isReadonly: readOnly }
      ),
      floorPlanDocuments: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.ATTACHMENT_GROUP_HEADERS.FLOOR_PLAN',
        this.transformDocument(this.dataContext.floorPlanDocuments),
        { isReadonly: readOnly }
      ),
      dhMarketArea: this._cf.createWithOptions(
        'LETTING.ADVERTISMENT.DHMARKET.AREA',
        this.dataContext.dhMarketArea ? this.dataContext.dhMarketArea : '',
        { isReadonly: readOnly }
      ),
      homeQOffice: this._cf.createReadonly('PROPERTY.HOMEQ_OFFICE', null),
    });

    this.AddSubscription(group.controls['platform'].valueChanges.subscribe({ next: value => this.platformSelected(value) }));
    group.controls['platform'].setValue(this.dataContext.platform);

    return group;
  }

  homeqOptions: HomeqOptions;
  get homeqSelected(): boolean {
    return this.form.controls.platform.value == LettingAdvertismentPlatformType.HomeQ;
  }

  dhMarketOptions: DhMarketOptions;
  get dhMarketSelected(): boolean {
    return this.form.controls.platform.value == LettingAdvertismentPlatformType.DhMarket;
  }

  get boplatsSverigeSelected(): boolean {
    return this.form.controls.platform.value == LettingAdvertismentPlatformType.BoplatsSverige;
  }

  get canUnpublishFromBoplatsSverige(): boolean {
    return (
      this.dataContext?.platform == LettingAdvertismentPlatformType.BoplatsSverige &&
      !!this.dataContext?.sentToPlatformAt &&
      !this.dataContext?.cancelledOnPlatformAt
    );
  }

  get platformTimestampLabel(): string {
    return this.dataContext?.cancelledOnPlatformAt ? 'LETTING.ADVERTISMENT.UNPUBLISHED_AT' : 'LETTING.ADVERTISMENT.PUBLISHED_AT';
  }

  get platformTimestamp(): Date {
    return this.dataContext?.cancelledOnPlatformAt ?? this.dataContext?.sentToPlatformAt;
  }

  platformSelected(platform: LettingAdvertismentPlatformType): void {
    switch (platform) {
      case LettingAdvertismentPlatformType.HomeQ: {
        this._dataService.getHomeqOptions().subscribe({ next: res => (this.homeqOptions = res) });
        break;
      }
      case LettingAdvertismentPlatformType.DhMarket: {
        this._dataService.getDhMarketOptions().subscribe({
          next: res => (this.dhMarketOptions = res),
          error: () => this.messageService.showWarning('LETTING.ADVERTISMENT.DHMARKET.ERROR_GET_OPTIONS'),
        });
        break;
      }
    }
  }

  protected init(advertisment: LettingAdvertisment) {
    this.AddSubscription(
      combineLatest([
        this._accountService.current,
        advertisment.isNew
          ? this._route.paramMap.pipe(
              switchMap(map => {
                const id = map.get('rentalObjectId');
                return id ? this._dataService.getDefaultsForRentalObject(id) : of(this.createModel());
              })
            )
          : of(advertisment),
      ]).subscribe({
        next: ([account, model]) => {
          if (account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)) {
            this.platformSelection.push(
              new SelectionItem('LETTING.ADVERTISMENT.PLATFORMS.DHMARKET', LettingAdvertismentPlatformType.DhMarket)
            );
            if (model.rentalObjectId) {
              this.AddSubscription(
                this._dataService.getRentalObjectTentants(model.rentalObjectId).subscribe({ next: res => (this.tenants = res) })
              );
            }
          }

          if (account.hasAccess(ModuleNames.Letting, Features.Letting_HomeQ)) {
            this.platformSelection.push(new SelectionItem('LETTING.ADVERTISMENT.PLATFORMS.HOMEQ', LettingAdvertismentPlatformType.HomeQ));
            if (model.rentalObjectId) {
              this.AddSubscription(
                this._dwellingService.getHomeqOffice(model.rentalObjectId).subscribe(res => {
                  this.homeQOfficeName = res.name ?? '';
                  this.form.controls['homeQOffice'].setValue(res);
                })
              );
            }
          }

          if (account.hasAccess(ModuleNames.Letting, Features.Letting_BoplatsSverige)) {
            this.canPublishDhMarketToBoplatsSverige = true;
            this.platformSelection.push(
              new SelectionItem('LETTING.ADVERTISMENT.PLATFORMS.BOPLATS_SVERIGE', LettingAdvertismentPlatformType.BoplatsSverige)
            );
          }

          super.init(model);
          this.isAdvertismentLoaded = true;
        },
      })
    );

    this.currentLang = this._languageService.currentCultureLang;
    this.AddSubscription(
      this._languageService.onLangChange.subscribe({
        next: e => {
          this.checkLangCode(e.lang);
        },
      })
    );
  }

  checkLangCode(lang: string) {
    switch (lang) {
      case 'sv':
        this.currentLang = 'sv-SE';
        break;
      case 'en':
        this.currentLang = 'en-GB';
        break;
      case 'da':
        this.currentLang = 'da-DK';
        break;
      case 'fi':
        this.currentLang = 'fi-FI';
        break;
      default:
        this.currentLang = 'sv-SE';
        break;
    }
  }

  prepareSave(): LettingAdvertisment {
    const model = super.prepareSave();

    if (this.dhMarketSelected) {
      model.rentalObjectDocuments = this.form.controls.rentalObjectPhotos.value;
      Object.assign(model, this.marketform.form.value);
      Object.assign(model, this.marketareaform.form.value);
    }

    if (this.homeqSelected) {
      Object.assign(model, this.homeqform.form.value);
      model.publishFrom = (this.homeqform.form.value.publishFrom as LocalDate)?.toDate()?.toISOString();
      model.publishTo = (this.homeqform.form.value.publishTo as LocalDate)?.toDate()?.toISOString();
    }

    if (this.boplatsSverigeSelected) {
      Object.assign(model, this.boplatsSverigeForm.form.value);
      model.publishFrom = (this.boplatsSverigeForm.form.value.publishFrom as LocalDate)?.toDate()?.toISOString();
      model.publishTo = (this.boplatsSverigeForm.form.value.publishTo as LocalDate)?.toDate()?.toISOString();
    }

    model.dhMarketArea = model.dhMarketArea ? model.dhMarketArea : null;

    return model;
  }

  saveAndSubmit(): void {
    if (
      !this.dataContext.id ||
      this.form.dirty ||
      this.marketform?.form.dirty ||
      this.marketareaform?.form.dirty ||
      this.homeqform?.form.dirty ||
      this.boplatsSverigeForm?.form.dirty
    ) {
      this.willBeSentToPlatform = new Subject<void>();
      this.onSubmit();
    } else {
      this.sendToPlatform(this.dataContext.id);
    }
  }

  onSubmit() {
    if (this.dataContext.approvedAt) {
      this.willBeSentToPlatform = null;
      return;
    }

    if (this.dhMarketSelected && (this.marketform.form.invalid || this.marketareaform.form.invalid)) {
      this.messageService.showError('LETTING.ADVERTISMENT.ERRORS.PLEASE_CHECK_THE_FORM');
      this.marketform.form.markAllAsTouched();
      this.marketareaform.form.markAllAsTouched();
      this.willBeSentToPlatform = null;
      return;
    }

    if (this.homeqSelected && this.homeqform.form.invalid) {
      this.messageService.showError('LETTING.ADVERTISMENT.ERRORS.PLEASE_CHECK_THE_FORM');
      this.homeqform.form.markAllAsTouched();
      this.willBeSentToPlatform = null;
      return;
    }

    if (this.boplatsSverigeSelected && this.boplatsSverigeForm.form.invalid) {
      this.messageService.showError('LETTING.ADVERTISMENT.ERRORS.PLEASE_CHECK_THE_FORM');
      this.boplatsSverigeForm.form.markAllAsTouched();
      this.willBeSentToPlatform = null;
      return;
    }

    // new ad, should allow save without changes
    if (!this.dataContext.id) {
      this.form.markAsDirty();
    }

    // not allowed to save a pristine main form, so dirty it
    if (
      this.marketform?.form.dirty ||
      this.marketareaform?.form.dirty ||
      this.homeqform?.form.dirty ||
      this.boplatsSverigeForm?.form.dirty
    ) {
      this.form.markAsDirty();
    }

    if (this.isSaving || !this.form || this.form.invalid || this.form.pristine) {
      // disable send to platform, but let super.onsubmit handle the form stuff
      this.willBeSentToPlatform = null;
    }

    return super.onSubmit();
  }

  protected override formSubmittedNew(id: string) {
    if (this.willBeSentToPlatform) {
      this.sendToPlatform(id);
    }
    this.formSubmitted();
  }

  protected override formSubmittedChange() {
    if (this.willBeSentToPlatform) {
      this.sendToPlatform(this.dataContext.id);
      this.willBeSentToPlatform.pipe(take(1)).subscribe({
        next: () => this.refreshAfterPlatformSend(this.dataContext.id),
      });
    }
    this.formSubmitted();
  }

  private sendToPlatform(id: string) {
    if (this.homeqSelected) {
      if (this.form.controls.homeQOffice?.value?.id) {
        this._dataService.sendToPlatform(id, this.form.controls.homeQOffice.value.id).subscribe({
          next: () => {
            if (this.willBeSentToPlatform) {
              this.willBeSentToPlatform.next();
            } else {
              this.refreshAfterPlatformSend(id);
            }
          },
          error: () => this.willBeSentToPlatform?.complete(),
        });
      } else {
        this._actionService
          .open(SetHomeqOfficesFormComponent, this._injector, null, { ...this.dataContext, id })
          .pipe(take(1))
          .subscribe({
            next: () => {
              if (this.willBeSentToPlatform) {
                this.willBeSentToPlatform.next();
              } else {
                this.refreshAfterPlatformSend(id);
              }
            },
            error: () => this.willBeSentToPlatform?.complete(),
            complete: () => this.willBeSentToPlatform?.complete(),
          });
      }
    } else {
      this._dataService.sendToPlatform(id).subscribe({
        next: () => {
          if (this.willBeSentToPlatform) {
            this.willBeSentToPlatform.next();
          } else {
            this.refreshAfterPlatformSend(id);
          }
        },
        error: () => this.willBeSentToPlatform?.complete(),
      });
    }
  }

  unpublishFromBoplatsSverige(): void {
    if (!this.canUnpublishFromBoplatsSverige || this.isSaving || this.isUnpublishingFromBoplatsSverige) {
      return;
    }

    this.messageService.showDialog('LETTING.ADVERTISMENT.UNPUBLISH_CONFIRM').subscribe({
      next: confirmed => {
        if (!confirmed) {
          return;
        }

        this.isUnpublishingFromBoplatsSverige = true;
        this._dataService
          .unpublishFromBoplatsSverige(this.dataContext.id)
          .pipe(finalize(() => (this.isUnpublishingFromBoplatsSverige = false)))
          .subscribe({
            next: cancelledOnPlatformAt => (this.dataContext.cancelledOnPlatformAt = new Date(cancelledOnPlatformAt)),
          });
      },
    });
  }

  cancel() {
    this.navigation.navigate(['my-property', 'dwelling', 'edit', this.dataContext.rentalObjectId]);
  }

  protected navigateAfterSave(id: string) {
    if (this.willBeSentToPlatform) {
      this.willBeSentToPlatform.pipe(take(1)).subscribe({
        next: () => {
          this.navigation.navigateChild(['edit', id]).then(() => this.refreshAfterPlatformSend(id));
        },
      });
    } else {
      this.navigation.navigateChild(['edit', id]);
    }
  }

  private refreshAfterPlatformSend(id: string): void {
    this._dataService
      .get(id)
      .pipe(take(1))
      .subscribe({
        next: model => {
          this.dataContext = model;
          this.form = this.getFormGroup();
          this.form.markAsPristine();
          this.willBeSentToPlatform = null;
          this.formSubmitted();
        },
        error: () => {
          this.willBeSentToPlatform = null;
          this.formSubmittedWithError();
        },
      });
  }

  private transformDocument(documents: IUploadedDocument[]) {
    return documents?.map(document => ({ id: document.connectionId, documentId: document.id, documentTitle: document.title })) ?? [];
  }
}
